import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ConsentIcon, TransportIcon, MessageIcon, HealthIcon, AcademicsIcon,
  DeviceIcon, ClockIcon, CheckIcon, DocumentIcon, CloseIcon, ArrowRightIcon,
} from "./icons";
import {
  User, Shield, FileText, Sparkles, ChevronRight, AlertCircle,
  CheckCircle2, Clock, Lock, Zap, Bell, Download, Calendar,
  TrendingUp, Award, Heart, BookOpen, Bus, FlaskConical, Trophy,
  Monitor, Coffee, Sun, Moon, Droplets, Wind, Star,
} from "lucide-react";
import { C, BRAND_GRADIENT, type Student } from "./tokens";
import { PremiumCard, SectionHeader, MiniBar, Pill, Avatar, ProgressRing } from "./primitives";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const EASE = [0.4, 0, 0.2, 1] as const;

type ConsentType = "trip" | "media" | "medical" | "sports" | "tech" | "aftercare";
type Priority = "high" | "medium" | "low";

type Consent = {
  id: string; type: ConsentType; title: string; description: string;
  student: string; studentId?: string; deadline: string; status: "pending" | "signed";
  priority: Priority; requestedBy: string;
  details: Record<string, string | string[] | undefined>;
  signedAt?: string; signedBy?: string;
};

const CONSENT_TYPES: Record<ConsentType, { icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>; color: string; bg: string; label: string; gradient: string }> = {
  trip: { icon: TransportIcon, color: C.orange, bg: C.orangeLight, label: "Field Trip", gradient: "linear-gradient(135deg, #C4A882, #A68A5A)" },
  media: { icon: MessageIcon, color: C.purple, bg: C.purpleLight, label: "Media", gradient: "linear-gradient(135deg, #8B6F47, #8B6F47)" },
  medical: { icon: HealthIcon, color: C.red, bg: C.redLight, label: "Medical", gradient: "linear-gradient(135deg, #B33A1A, #A68A5A)" },
  sports: { icon: AcademicsIcon, color: "#1C1917", bg: "#EDE4D4", label: "Sports", gradient: "#1C1917" },
  tech: { icon: DeviceIcon, color: C.blue, bg: C.blueLight, label: "Technology", gradient: "linear-gradient(135deg, #7B1416, #A42022)" },
  aftercare: { icon: ClockIcon, color: C.teal, bg: C.tealLight, label: "Aftercare", gradient: "linear-gradient(135deg, #C4A882, #30D5C8)" },
};

export function ConsentView({ students }: { students: Student[] }) {
  const [selectedConsent, setSelectedConsent] = useState<Consent | null>(null);
  const [filterStatus, setFilterStatus] = useState<"all" | "pending" | "signed">("all");
  const [filterType, setFilterType] = useState<ConsentType | "all">("all");
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  const consents: Consent[] = useMemo(() => [
    { id: "c1", type: "trip", title: "Science Lab Trip — Uganda Museum", description: "Field trip to Uganda Museum for P6 students. Bus departs at 8:00 AM, returns by 4:00 PM.", student: students[0]?.full_name ?? "—", studentId: students[0]?.id, deadline: "Jul 7, 2026", status: "pending", priority: "high", requestedBy: "Mr. Okello Isaac (Class Teacher)", details: { date: "July 10, 2026", cost: "UGX 25,000", transport: "School Bus", requirements: ["Packed lunch", "Water bottle", "Comfortable shoes"] } },
    { id: "c2", type: "media", title: "Photography & Media Consent", description: "Permission for the school to photograph and video your child for school publications.", student: students[1]?.full_name ?? students[0]?.full_name ?? "—", studentId: students[1]?.id ?? students[0]?.id, deadline: "Jul 15, 2026", status: "signed", priority: "medium", signedAt: "Jul 2, 2026", signedBy: "Parent (Digital Signature)", requestedBy: "Communications Office", details: { scope: ["Yearbook photos", "School website", "Newsletter"], restrictions: "No full names without explicit consent" } },
    { id: "c3", type: "medical", title: "Medical Treatment Authorization", description: "Authorization for school nurse to provide first aid and basic medical treatment.", student: students[0]?.full_name ?? "—", studentId: students[0]?.id, deadline: "Jul 20, 2026", status: "pending", priority: "high", requestedBy: "Health Office - Nurse Namusoke", details: { includes: ["First aid", "Paracetamol", "Antihistamines", "Emergency transport"], excludes: ["Surgical procedures", "Prescription medications"] } },
    { id: "c4", type: "sports", title: "Inter-House Sports Day Participation", description: "Consent for child to participate in Inter-House athletics competition.", student: students[1]?.full_name ?? students[0]?.full_name ?? "—", studentId: students[1]?.id ?? students[0]?.id, deadline: "Jul 22, 2026", status: "pending", priority: "medium", requestedBy: "Coach Wasswa David", details: { events: ["100m Sprint", "Long Jump", "4x100m Relay"], requirements: ["Sports kit", "Running shoes"] } },
    { id: "c5", type: "tech", title: "Technology & Internet Usage Agreement", description: "Agreement for student use of school computers and internet resources.", student: students[0]?.full_name ?? "—", studentId: students[0]?.id, deadline: "Aug 1, 2026", status: "pending", priority: "low", requestedBy: "IT Department", details: { allowed: ["Educational websites", "School portal"], restricted: ["Social media", "Gaming sites"] } },
    { id: "c6", type: "aftercare", title: "After School Care Enrollment", description: "Enrollment in after-school care program from 3:30 PM to 6:00 PM.", student: students[1]?.full_name ?? students[0]?.full_name ?? "—", studentId: students[1]?.id ?? students[0]?.id, deadline: "Jul 10, 2026", status: "signed", priority: "medium", signedAt: "Jun 28, 2026", signedBy: "Parent (Digital Signature)", requestedBy: "Aftercare Coordinator", details: { timing: "3:30 PM - 6:00 PM", days: "Monday - Friday", cost: "UGX 150,000/month" } },
  ], [students]);

  const filtered = consents.filter(c => {
    const matchesStatus = filterStatus === "all" || c.status === filterStatus;
    const matchesType = filterType === "all" || c.type === filterType;
    return matchesStatus && matchesType;
  });
  const pendingCount = consents.filter(c => c.status === "pending").length;
  const signedCount = consents.filter(c => c.status === "signed").length;
  const urgentCount = consents.filter(c => c.status === "pending" && c.priority === "high").length;
  const completionPct = consents.length > 0 ? (signedCount / consents.length) * 100 : 0;

  const signConsent = () => {
    toast.success("Consent signed successfully! A confirmation email has been sent.");
    setSelectedConsent(null);
  };

  return (
    <div className="space-y-6 sm:space-y-8 max-w-6xl mx-auto">
      {/* ═════════ HERO ═════════ */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: EASE }}
        className="relative overflow-hidden rounded-[24px] border shadow-float"
        style={{ background: `linear-gradient(135deg, ${C.purple} 0%, #6366f1 100%)`, borderColor: "rgba(255,255,255,0.15)" }}
      >
        {/* Ambient glows */}
        <motion.div className="absolute -right-12 -top-12 h-44 w-44 rounded-full opacity-20" style={{ background: "radial-gradient(circle, white, transparent 70%)" }} animate={{ scale: [1, 1.2, 1], opacity: [0.15, 0.25, 0.15] }} transition={{ duration: 6, repeat: Infinity }} />
        <motion.div className="absolute -left-16 -bottom-16 h-52 w-52 rounded-full opacity-10" style={{ background: "radial-gradient(circle, #D4B89A, transparent 70%)" }} animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 8, repeat: Infinity, delay: 2 }} />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "20px 20px" }} />

        <div className="relative px-6 sm:px-10 py-8 sm:py-10">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-12 w-12 rounded-2xl flex items-center justify-center bg-white/15 backdrop-blur-md">
              <ConsentIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white tracking-tight">Consent Centre</h2>
              <p className="text-sm text-white/70">Digital authorizations & permissions</p>
            </div>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            {[
              { label: "Pending Action", value: pendingCount, color: C.amber, icon: Clock },
              { label: "Urgent", value: urgentCount, color: C.red, icon: AlertCircle },
              { label: "Signed", value: signedCount, color: C.green, icon: CheckCircle2 },
              { label: "Completion", value: `${Math.round(completionPct)}%`, color: "#D4B89A", icon: TrendingUp },
            ].map((s, i) => (
              <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.08 }} whileHover={{ y: -3 }} className="rounded-2xl bg-white/15 backdrop-blur-md px-4 py-3.5 border border-white/10">
                <div className="flex items-center gap-2 mb-1">
                  <s.icon className="h-3.5 w-3.5" style={{ color: s.color }} />
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-white/60">{s.label}</p>
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-white">{s.value}</p>
              </motion.div>
            ))}
          </div>

          {/* Progress bar */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-white/60">Overall Completion</span>
              <span className="text-xs font-bold text-white">{Math.round(completionPct)}%</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden bg-white/10">
              <motion.div initial={{ width: 0 }} animate={{ width: `${completionPct}%` }} transition={{ duration: 1, ease: "easeOut" }} className="h-full rounded-full" style={{ background: "#1C1917" }} />
            </div>
          </div>
        </div>
      </motion.div>

      {/* ═════════ FILTERS ═════════ */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        {/* Status filter */}
        <div className="flex items-center gap-2">
          {[
            { key: "all", label: "All Requests", count: consents.length, icon: DocumentIcon },
            { key: "pending", label: "Pending", count: pendingCount, icon: ClockIcon },
            { key: "signed", label: "Signed", count: signedCount, icon: CheckIcon },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilterStatus(tab.key as any)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all"
              style={filterStatus === tab.key
                ? { background: C.accent, color: "white", boxShadow: "0 4px 12px rgba(0,113,227,0.25)" }
                : { background: "white", color: C.textMid, border: `1px solid ${C.cardBorder}` }}
            >
              <tab.icon className="h-4 w-4" />{tab.label}
              <span className="rounded-full px-2 py-0.5 text-xs" style={{ background: filterStatus === tab.key ? "rgba(255,255,255,0.2)" : C.bg, color: filterStatus === tab.key ? "white" : C.textMuted }}>{tab.count}</span>
            </button>
          ))}
        </div>
        {/* Type filter chips */}
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar sm:ml-auto">
          <button onClick={() => setFilterType("all")} className="px-3 py-1.5 rounded-full text-xs font-semibold capitalize whitespace-nowrap transition" style={filterType === "all" ? { background: C.purple, color: "white" } : { background: "white", color: C.textMuted, border: `1px solid ${C.cardBorder}` }}>All Types</button>
          {(Object.keys(CONSENT_TYPES) as ConsentType[]).map(t => (
            <button key={t} onClick={() => setFilterType(t)} className="px-3 py-1.5 rounded-full text-xs font-semibold capitalize whitespace-nowrap transition" style={filterType === t ? { background: CONSENT_TYPES[t].color, color: "white" } : { background: "white", color: C.textMuted, border: `1px solid ${C.cardBorder}` }}>{CONSENT_TYPES[t].label}</button>
          ))}
        </div>
      </div>

      {/* ═════════ CONSENT CARDS ═════════ */}
      <div className="grid gap-5 grid-cols-1 lg:grid-cols-2">
        {filtered.map((consent, i) => {
          const typeConfig = CONSENT_TYPES[consent.type];
          const Icon = typeConfig.icon;
          const daysLeft = Math.ceil((new Date(consent.deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
          const isUrgent = daysLeft <= 3 && consent.status === "pending";
          const isHovered = hoveredCard === consent.id;
          return (
            <motion.div
              key={consent.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05, ease: EASE }}
              whileHover={{ y: -4 }}
              onHoverStart={() => setHoveredCard(consent.id)}
              onHoverEnd={() => setHoveredCard(null)}
              onClick={() => setSelectedConsent(consent)}
              className="relative overflow-hidden rounded-[20px] border bg-white cursor-pointer transition-all shadow-premium"
              style={{
                borderColor: consent.status === "pending" ? (isUrgent ? `${C.red}40` : `${C.amber}30`) : C.cardBorder,
                boxShadow: isUrgent ? `0 8px 32px ${C.red}15` : isHovered ? `0 8px 32px ${typeConfig.color}15` : undefined,
              }}
            >
              {/* Gradient accent bar */}
              <div className="h-1" style={{ background: consent.status === "pending" ? (isUrgent ? `linear-gradient(90deg, ${C.red}, ${C.amber})` : typeConfig.gradient) : `linear-gradient(90deg, ${C.green}, #D4B89A)` }} />
              {/* Hover glow */}
              <div className="absolute inset-0 pointer-events-none transition-opacity" style={{ opacity: isHovered ? 1 : 0, background: `radial-gradient(circle at 50% 0%, ${typeConfig.color}08, transparent 60%)` }} />

              <div className="relative p-5">
                {/* Header */}
                <div className="flex items-start gap-4 mb-4">
                  <div className="h-12 w-12 rounded-[14px] flex items-center justify-center shrink-0 transition-transform" style={{ background: typeConfig.bg, transform: isHovered ? "scale(1.1)" : "scale(1)" }}>
                    <Icon className="h-5 w-5" style={{ color: typeConfig.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: typeConfig.bg, color: typeConfig.color }}>{typeConfig.label}</span>
                      {consent.priority === "high" && consent.status === "pending" && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: `${C.red}15`, color: C.red }}>High Priority</span>}
                    </div>
                    <h4 className="text-sm font-bold truncate" style={{ color: C.text }}>{consent.title}</h4>
                    <p className="text-xs" style={{ color: C.textMuted }}>Requested by: {consent.requestedBy}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <Badge style={{ background: consent.status === "pending" ? C.amberLight : C.greenLight, color: consent.status === "pending" ? C.amber : C.green, border: "none" }}>{consent.status === "pending" ? "Pending" : "Signed"}</Badge>
                    {consent.status === "pending" && <p className="text-[10px] mt-1 font-medium" style={{ color: isUrgent ? C.red : C.textMuted }}>{daysLeft <= 0 ? "Overdue" : daysLeft === 1 ? "Due tomorrow" : `${daysLeft} days left`}</p>}
                    {consent.status === "signed" && <p className="text-[10px] mt-1" style={{ color: C.textMuted }}>{consent.signedAt}</p>}
                  </div>
                </div>

                {/* Description */}
                <p className="text-xs leading-relaxed mb-4" style={{ color: C.textMid }}>{consent.description}</p>

                {/* Deadline countdown bar for pending */}
                {consent.status === "pending" && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-semibold uppercase tracking-tight" style={{ color: C.textMuted }}>Time Remaining</span>
                      <span className="text-[10px] font-bold" style={{ color: isUrgent ? C.red : C.amber }}>{daysLeft <= 0 ? "Overdue" : `${daysLeft} days`}</span>
                    </div>
                    <MiniBar value={Math.max(0, Math.min(100, (daysLeft / 30) * 100))} max={100} color={isUrgent ? C.red : C.amber} />
                  </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between pt-3 border-t border-[#E2D6C0]/60">
                  <div className="flex items-center gap-2 text-xs" style={{ color: C.textMuted }}>
                    <User className="h-3.5 w-3.5" />{consent.student.split(" ")[0]}
                  </div>
                  {consent.status === "pending" ? (
                    <Button size="sm" className="h-8 text-xs rounded-xl" style={{ background: typeConfig.color, color: "white" }} onClick={(e) => { e.stopPropagation(); setSelectedConsent(consent); }}>
                      Review & Sign<ArrowRightIcon className="h-3 w-3 ml-1.5" />
                    </Button>
                  ) : (
                    <div className="flex items-center gap-1.5 text-xs font-medium" style={{ color: C.green }}>
                      <CheckIcon className="h-3.5 w-3.5" />Signed
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* ═════════ CONSENT HISTORY TIMELINE ═════════ */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.5 }}>
        <PremiumCard>
          <SectionHeader title="Consent History" subtitle="Recently signed authorizations" icon={FileText} iconBg={C.greenLight} />
          <div className="p-5 sm:p-6">
            <div className="relative pl-6">
              <div className="absolute left-2 top-2 bottom-2 w-px" style={{ background: C.border }} />
              {consents.filter(c => c.status === "signed").map((c, i) => {
                const typeConfig = CONSENT_TYPES[c.type];
                return (
                  <motion.div key={c.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 + i * 0.1 }} whileHover={{ x: 4 }} className="relative flex items-center gap-4 pb-5 last:pb-0 cursor-pointer">
                    <div className="absolute -left-[18px] w-4 h-4 rounded-full flex items-center justify-center" style={{ background: C.green, boxShadow: "0 0 0 3px white" }}>
                      <CheckIcon className="w-2.5 h-2.5 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium" style={{ color: C.text }}>{c.title}</p>
                      <p className="text-xs" style={{ color: C.textMuted }}>Signed {c.signedAt} · {c.signedBy}</p>
                    </div>
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: typeConfig.bg, color: typeConfig.color }}>{typeConfig.label}</span>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </PremiumCard>
      </motion.div>

      {/* ═════════ MODAL ═════════ */}
      <AnimatePresence>
        {selectedConsent && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(16px)" }}
            onClick={() => setSelectedConsent(null)}
          >
            <motion.div
              initial={{ scale: 0.94, y: 24, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.94, y: 24, opacity: 0 }}
              transition={{ type: "spring", damping: 28, stiffness: 320 }}
              className="w-full max-w-xl rounded-[24px] overflow-hidden shadow-2xl bg-white"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal header — gradient */}
              <div className="relative px-6 py-5 overflow-hidden" style={{ background: `linear-gradient(135deg, ${CONSENT_TYPES[selectedConsent.type].color} 0%, ${CONSENT_TYPES[selectedConsent.type].color}cc 100%)` }}>
                <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full opacity-20" style={{ background: "white" }} />
                <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "16px 16px" }} />
                <div className="relative flex items-start gap-4">
                  <div className="h-14 w-14 rounded-2xl flex items-center justify-center bg-white/20 backdrop-blur-md">
                    {React.createElement(CONSENT_TYPES[selectedConsent.type].icon, { className: "h-7 w-7 text-white" })}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wider text-white/70 mb-1">{CONSENT_TYPES[selectedConsent.type].label} Request</p>
                    <h3 className="text-lg font-bold text-white">{selectedConsent.title}</h3>
                  </div>
                  <button onClick={() => setSelectedConsent(null)} className="h-9 w-9 rounded-xl flex items-center justify-center bg-white/10 hover:bg-white/20 transition">
                    <CloseIcon className="h-4 w-4 text-white" />
                  </button>
                </div>
              </div>

              {/* Modal body */}
              <div className="p-6 space-y-5 max-h-[60vh] overflow-y-auto">
                <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: C.bg }}>
                  <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: `${C.accent}15` }}>
                    <User className="h-5 w-5" style={{ color: C.accent }} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Requested By</p>
                    <p className="text-sm font-semibold" style={{ color: C.text }}>{selectedConsent.requestedBy}</p>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: C.textMuted }}>Description</p>
                  <p className="text-sm leading-relaxed" style={{ color: C.textMid }}>{selectedConsent.description}</p>
                </div>

                {selectedConsent.details && (
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(selectedConsent.details).map(([key, value]) => {
                      if (Array.isArray(value)) {
                        return (
                          <div key={key} className="p-3 rounded-xl col-span-2" style={{ background: C.bg }}>
                            <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: C.textMuted }}>{key.replace(/([A-Z])/g, " $1")}</p>
                            <div className="flex flex-wrap gap-1.5">
                              {(value as string[]).map((item, idx) => (
                                <span key={idx} className="text-xs px-2.5 py-1 rounded-full" style={{ background: "white", color: C.textMid, border: `1px solid ${C.cardBorder}` }}>{item}</span>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      return <div key={key} className="p-3 rounded-xl" style={{ background: C.bg }}>
                        <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>{key.replace(/([A-Z])/g, " $1")}</p>
                        <p className="text-sm font-medium mt-0.5" style={{ color: C.text }}>{String(value)}</p>
                      </div>;
                    })}
                  </div>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-[#E2D6C0]/60">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" style={{ color: C.textMuted }} />
                    <div>
                      <p className="text-xs" style={{ color: C.textMuted }}>Student</p>
                      <p className="text-sm font-semibold" style={{ color: C.text }}>{selectedConsent.student}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs" style={{ color: C.textMuted }}>Deadline</p>
                    <p className="text-sm font-semibold" style={{ color: C.text }}>{selectedConsent.deadline}</p>
                  </div>
                </div>
              </div>

              {/* Modal footer */}
              <div className="px-6 py-4 border-t border-[#E2D6C0]/60 flex gap-3">
                {selectedConsent.status === "pending" ? (
                  <>
                    <Button className="flex-1 h-11 rounded-xl text-white font-semibold" style={{ background: C.green }} onClick={signConsent}>
                      <CheckIcon className="h-4 w-4 mr-2" />Grant Consent
                    </Button>
                    <Button variant="outline" className="flex-1 h-11 rounded-xl" style={{ borderColor: C.red, color: C.red }}>Decline</Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" className="flex-1 h-11 rounded-xl" style={{ borderColor: C.textMid, color: C.textMid }}>View Signed Document</Button>
                    <Button variant="outline" className="h-11 rounded-xl" style={{ borderColor: C.amber, color: C.amber }}>Request Changes</Button>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
